"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";

import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { useFaqDrawer } from "@/lib/faq-context";
import { resolveTopicName, type FaqTopic } from "@/lib/faq-topics";
import {
  faqAnchor,
  faqInRegion,
  faqUrl,
  faqAnswer,
  filterFaqsByTopicAndRegion,
  topicSlug,
  topicUrl,
  type FaqStory,
} from "@/lib/faqs";
import type { Region } from "@/lib/region";
import {
  clearRouteFocusSuppressed,
  isRouteFocusSuppressed,
  suppressRouteFocusReset,
} from "@/lib/route-focus";
import { normalizeQuery, tokenizeQuery } from "@/lib/search";

import { FaqRichText } from "./faq-richtext";

const RESULTS_PER_PAGE = 12;

function SearchIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="9" cy="9" r="6" />
      <path d="m17 17-3.5-3.5" />
    </svg>
  );
}

function ChevronArrow() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export default function FaqTopicPage({
  topicValue,
  faqs: initialFaqs,
  topics,
  region,
}: {
  topicValue: string;
  faqs: FaqStory[];
  topics: FaqTopic[];
  region: Region;
}) {
  // Merge server props with FAQ context (context wins per-uuid so live
  // Storyblok bridge edits flow through), then scope to the current
  // region — the context holds every region's FAQs.
  const { faqs: contextFaqs } = useFaqDrawer();
  const faqs = useMemo(() => {
    const byUuid = new Map(initialFaqs.map((f) => [f.uuid, f]));
    for (const f of contextFaqs) byUuid.set(f.uuid, f);
    return Array.from(byUuid.values()).filter((f) => faqInRegion(f, region));
  }, [contextFaqs, initialFaqs, region]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const setQueryAndReset = (next: string) => {
    setQuery(next);
    setPage(1);
  };

  const { ref: mobileTopicsRef, rovingProps: mobileTopicsProps } =
    useRovingFocus<HTMLUListElement>();
  const { ref: desktopTopicsRef, rovingProps: desktopTopicsProps } =
    useRovingFocus<HTMLUListElement>();


  const markTopicNav = (value: string) => {
    if (value !== topicValue) suppressRouteFocusReset();
  };


  useEffect(() => {
    if (!isRouteFocusSuppressed()) return;
    const raf = requestAnimationFrame(() => {
      for (const list of [desktopTopicsRef.current, mobileTopicsRef.current]) {
        const active = list?.querySelector<HTMLElement>('[aria-current="page"]');
        if (active && active.offsetParent !== null) {
          active.focus();
          break;
        }
      }
      clearRouteFocusSuppressed();
    });
    return () => cancelAnimationFrame(raf);
  }, [topicValue, desktopTopicsRef, mobileTopicsRef]);

  useEffect(() => {
    const ul = desktopTopicsRef.current;
    const questions = ul?.closest('[class*="lg:flex"]')
      ?.lastElementChild as HTMLElement | null;
    if (!ul || !questions || questions.contains(ul)) return;
    const tabbable =
      'a[href]:not([tabindex="-1"]),button:not([disabled]):not([tabindex="-1"]),input:not([disabled]):not([tabindex="-1"]),select:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]):not([tabindex="-1"]),[tabindex]:not([tabindex="-1"])';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !e.shiftKey || !ul.offsetParent) return;
      const first = questions.querySelector<HTMLElement>(tabbable);
      if (first && first === document.activeElement) {
        e.preventDefault();
        (
          ul.querySelector<HTMLElement>('[aria-current="page"]') ??
          ul.querySelector<HTMLElement>('[data-roving][tabindex="0"]') ??
          ul.querySelector<HTMLElement>("[data-roving]")
        )?.focus();
      }
    };
    questions.addEventListener("keydown", onKeyDown);
    return () => questions.removeEventListener("keydown", onKeyDown);
  }, [desktopTopicsRef]);

  const topicName = resolveTopicName(topicValue, topics);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of faqs) {
      const slug = topicSlug(f.content?.topic);
      if (!slug) continue;
      m.set(slug, (m.get(slug) ?? 0) + 1);
    }
    return m;
  }, [faqs]);

  // Every topic that has FAQs, named from the datasource — so topics an
  // editor adds appear in the sidebar without a code change.
  const availableTopics = useMemo<FaqTopic[]>(
    () =>
      Array.from(counts.keys())
        .map((value) => ({ value, name: resolveTopicName(value, topics) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [counts, topics],
  );

  const topicFaqs = useMemo(
    () => filterFaqsByTopicAndRegion(faqs, topicValue, region),
    [faqs, topicValue, region],
  );

  const normalised = normalizeQuery(query);
  const tokens = useMemo(() => tokenizeQuery(normalised), [normalised]);
  const matches = useMemo(() => {
    if (!normalised || tokens.length === 0) return [];
    return faqs.filter((faq) => {
      const name = faq.name.toLowerCase();
      return tokens.every((t) => name.includes(t));
    });
  }, [faqs, normalised, tokens]);

  const totalPages = Math.max(1, Math.ceil(matches.length / RESULTS_PER_PAGE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pagedMatches = matches.slice(
    (currentPage - 1) * RESULTS_PER_PAGE,
    currentPage * RESULTS_PER_PAGE,
  );

  return (
    <article>
      <section className="bg-dusk-01">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(2rem,4vw,3.5rem)] pb-[clamp(2.5rem,5vw,3.5rem)]">
          <div className="mx-auto max-w-[760px] text-center">
            <h1 className="text-[clamp(2.25rem,5vw,4rem)] font-bold leading-[1.08] tracking-[-0.01em] text-purple-05">
              {topicName}
            </h1>
            <p className="mt-3 text-body text-black/65 lg:text-h6 lg:leading-normal">
              {topicFaqs.length}{" "}
              {topicFaqs.length === 1 ? "question" : "questions"} in this topic
            </p>
          </div>

          <div className="mx-auto mt-6 max-w-[640px] lg:mt-8">
            <div className="relative">
              <span className="pointer-events-none absolute top-1/2 left-5 -translate-y-1/2 text-purple-03">
                <SearchIcon />
              </span>
              <input
                type="search"
                name="faq-search"
                value={query}
                onChange={(e) => setQueryAndReset(e.target.value)}
                placeholder="Search across all FAQs…"
                aria-label="Search FAQs"
                autoComplete="off"
                className="h-14 w-full rounded-full border border-purple-01/40 bg-white pr-14 pl-12 text-body text-black placeholder:text-black/55 outline-none transition-shadow focus:border-purple-03 focus:shadow-[0_4px_20px_rgba(106,95,218,0.18)] [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQueryAndReset("")}
                  aria-label="Clear search"
                  className="absolute top-1/2 right-3 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/5 text-black/60 transition-colors hover:bg-black/10 hover:text-black"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden
                  >
                    <path d="m3 3 8 8M11 3l-8 8" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="border-b border-black/8 bg-white lg:hidden">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)]">
          <div className="-mx-[clamp(1.25rem,4vw,2.5rem)] overflow-x-auto px-[clamp(1.25rem,4vw,2.5rem)] py-5 [-ms-overflow-style:none] scrollbar-none [&::-webkit-scrollbar]:hidden">
            <ul
              ref={mobileTopicsRef}
              className="flex w-max gap-2"
              {...mobileTopicsProps}
            >
              {availableTopics.map((t) => {
                const isActive = t.value === topicValue;
                return (
                  <li key={t.value}>
                    <Link
                      href={topicUrl(t.value, region)}
                      data-roving
                      onClick={() => markTopicNav(t.value)}
                      aria-current={isActive ? "page" : undefined}
                      className={`flex h-10 shrink-0 items-center rounded-full px-5 text-button font-semibold transition-colors ${
                        isActive
                          ? "bg-purple-05 text-white"
                          : "bg-dusk-01 text-black hover:bg-purple-05/10"
                      }`}
                    >
                      {t.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,4rem)]">
        <div className="lg:flex lg:items-start lg:gap-24">
          <div className="hidden lg:sticky lg:top-24 lg:block lg:w-[260px] lg:shrink-0 lg:self-start">
            <p className="text-button font-bold tracking-[0.04em] text-black/55 uppercase">
              Topics
            </p>
            <nav
              aria-label="FAQ topics"
              className="scrollbar-none mt-4 max-h-[calc(100vh-8rem)] overflow-y-auto py-1 pr-3 pl-1"
            >
              <ul
                ref={desktopTopicsRef}
                className="flex flex-col gap-0.5"
                {...desktopTopicsProps}
              >
                {availableTopics.map((t) => {
                  const isActive = t.value === topicValue;
                  const topicCount = counts.get(t.value) ?? 0;
                  return (
                    <li key={t.value}>
                      <Link
                        href={topicUrl(t.value, region)}
                        data-roving
                        onClick={() => markTopicNav(t.value)}
                        aria-current={isActive ? "page" : undefined}
                        className={`flex items-center justify-between gap-3 rounded-[10px] px-4 py-2.5 text-button font-semibold transition-colors ${
                          isActive
                            ? "bg-purple-05 text-white"
                            : "text-black hover:bg-purple-05/10"
                        }`}
                      >
                        <span className="leading-tight">{t.name}</span>
                        <span
                          className={`shrink-0 text-caption tabular-nums ${
                            isActive ? "text-white/70" : "text-black/55"
                          }`}
                        >
                          {topicCount}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          <div className="lg:min-w-0 lg:flex-1">
            <h2 className="mb-6 text-button font-bold tracking-[0.04em] text-black/55 uppercase">
              Questions
            </h2>
            {normalised ? (
              <SearchResults
                matches={matches}
                pagedMatches={pagedMatches}
                query={normalised}
                region={region}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            ) : (
              <FaqList faqs={topicFaqs} region={region} />
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function useActiveHash(): string {
  const [hash, setHash] = useState("");
  useEffect(() => {
    const read = () => setHash(window.location.hash.slice(1));
    read();
    window.addEventListener("hashchange", read);
    window.addEventListener("popstate", read);
    return () => {
      window.removeEventListener("hashchange", read);
      window.removeEventListener("popstate", read);
    };
  }, []);
  return hash;
}

function FaqList({ faqs, region }: { faqs: FaqStory[]; region: Region }) {
  const activeHash = useActiveHash();
  const { ref: listRef, rovingProps } = useRovingFocus<HTMLUListElement>();
  const [openUid, setOpenUid] = useState<string | null>(
    faqs[0]?.uuid ?? null,
  );
  const [highlightedUid, setHighlightedUid] = useState<string | null>(null);

  // Synced during render via prevHash to avoid setState in an effect.
  const [prevHash, setPrevHash] = useState(activeHash);
  if (prevHash !== activeHash) {
    setPrevHash(activeHash);
    if (activeHash) {
      const match = faqs.find((f) => faqAnchor(f.slug) === activeHash);
      if (match) {
        setOpenUid(match.uuid);
        setHighlightedUid(match.uuid);
      }
    }
  }

  function toggle(uuid: string, question?: string) {
    const isOpen = openUid === uuid;
    setOpenUid(isOpen ? null : uuid);
    if (!isOpen) {
      track(ANALYTICS_EVENTS.FAQ_OPENED, { question });
    }
    if (highlightedUid && uuid !== highlightedUid) {
      setHighlightedUid(null);
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }

  if (faqs.length === 0) {
    return (
      <p className="text-body text-black/60">No FAQs in this topic yet.</p>
    );
  }
  return (
    <div className="w-full">
      <ul ref={listRef} className="flex flex-col" {...rovingProps}>
        {faqs.map((faq, i) => {
          const anchor = faqAnchor(faq.slug);
          const isOpen = openUid === faq.uuid;
          const isHighlighted = highlightedUid === faq.uuid;
          const isFirst = i === 0;
          const isLast = i === faqs.length - 1;
          return (
            <li
              key={faq.uuid}
              id={anchor}
              className={`scroll-mt-28 border-t transition-[background-color,border-color] duration-300 ${
                isFirst ? "border-purple-02" : "border-purple-01"
              } ${isLast ? "border-b border-b-purple-01" : ""} ${
                isHighlighted
                  ? "bg-dusk-01 px-4"
                  : ""
              }`}
            >
              <button
                type="button"
                data-roving
                onClick={() => toggle(faq.uuid, faq.name)}
                aria-expanded={isOpen}
                className="flex w-full cursor-pointer items-start justify-between gap-6 py-5 text-left"
              >
                <span className="flex-1 text-body font-bold leading-[1.56] text-[#212126] lg:text-h6">
                  {faq.name}
                </span>
                <span
                  aria-hidden
                  className="shrink-0 text-[28px] leading-none text-purple-03"
                >
                  {isOpen ? "–" : "+"}
                </span>
              </button>
              <div
                inert={!isOpen}
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="pb-8 pr-0 text-body leading-[1.56] text-[#5c5c66] lg:pr-12">
                    <FaqRichText node={faqAnswer(faq, region)} />
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SearchResults({
  matches,
  pagedMatches,
  query,
  region,
  currentPage,
  totalPages,
  onPageChange,
}: {
  matches: FaqStory[];
  pagedMatches: FaqStory[];
  query: string;
  region: Region;
  currentPage: number;
  totalPages: number;
  onPageChange: (n: number) => void;
}) {
  const { ref: gridRef, rovingProps } = useRovingFocus<HTMLUListElement>();
  const resultsTopRef = useRef<HTMLSpanElement>(null);

  const goToPage = (n: number) => {
    onPageChange(n);
    // Back to the top, with focus just before the results so the next Tab
    // lands on the first card.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      resultsTopRef.current?.focus({ preventScroll: true });
    });
  };

  if (matches.length === 0) {
    return (
      <div className="mx-auto max-w-[800px] rounded-[16px] border border-black/10 bg-white p-8 text-center">
        <p className="text-h6 font-bold text-black">
          No questions match “{query}”
        </p>
        <p className="mt-2 text-body text-black/60">
          Try a different word or browse the topics above.
        </p>
      </div>
    );
  }
  return (
    <section>
      <span ref={resultsTopRef} tabIndex={-1} className="sr-only" />
      <h2 className="text-h4 font-bold leading-[1.12] text-purple-05 lg:text-h2 lg:leading-[1.054]">
        {matches.length} {matches.length === 1 ? "result" : "results"} for “
        {query}”
      </h2>
      <ul
        ref={gridRef}
        className="mt-6 grid gap-3 md:grid-cols-2"
        {...rovingProps}
      >
        {pagedMatches.map((faq) => (
          <li key={faq.uuid}>
            <Link
              href={faqUrl(faq, region)}
              data-roving
              className="group flex h-full items-center justify-between gap-4 rounded-[12px] border border-black/10 bg-white px-5 py-4 transition-colors hover:border-purple-03 hover:bg-purple-05/5 lg:px-5 lg:py-5"
            >
              <span className="text-body font-semibold leading-tight text-black lg:text-[17px]">
                {faq.name}
              </span>
              <span className="shrink-0 text-purple-03 transition-transform group-hover:translate-x-0.5">
                <ChevronArrow />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {totalPages > 1 && (
        <Pagination
          current={currentPage}
          total={totalPages}
          onChange={goToPage}
        />
      )}
    </section>
  );
}

function pageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...set]
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

function Pagination({
  current,
  total,
  onChange,
}: {
  current: number;
  total: number;
  onChange: (n: number) => void;
}) {
  const window = pageWindow(current, total);
  const linkClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 cursor-pointer items-center justify-center rounded-full border border-black/10 px-3 text-button font-semibold text-purple-05 transition-colors hover:border-purple-03 hover:text-purple-03";
  const activeClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 items-center justify-center rounded-full bg-purple-03 px-3 text-button font-semibold text-white";
  const disabledClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 items-center justify-center rounded-full border border-black/10 px-3 text-button font-semibold text-black/30";

  // Entry tab stop is the next page so Tab into the pagination is ready to
  // advance. Roving items: [prev?, window number buttons, next?].
  const homeIndex =
    (current > 1 ? 1 : 0) +
    window.filter((w) => typeof w === "number" && w < current + 1).length;
  const { ref: navRef, rovingProps } = useRovingFocus<HTMLElement>({
    homeIndex,
  });

  return (
    <nav
      ref={navRef}
      aria-label="Search results pagination"
      className="mt-10 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
      {...rovingProps}
    >
      {current > 1 ? (
        <button
          type="button"
          data-roving
          onClick={() => onChange(current - 1)}
          aria-label="Previous page"
          className={linkClass}
        >
          ←
        </button>
      ) : (
        <span aria-hidden className={disabledClass}>
          ←
        </span>
      )}
      {window.map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} aria-hidden className="px-1 text-black/55">
            …
          </span>
        ) : (
          // The current page stays a focusable button so focus isn't dropped.
          <button
            key={p}
            type="button"
            data-roving
            onClick={() => onChange(p)}
            aria-current={p === current ? "page" : undefined}
            className={p === current ? activeClass : linkClass}
          >
            {p}
          </button>
        ),
      )}
      {current < total ? (
        <button
          type="button"
          data-roving
          onClick={() => onChange(current + 1)}
          aria-label="Next page"
          className={linkClass}
        >
          →
        </button>
      ) : (
        <span aria-hidden className={disabledClass}>
          →
        </span>
      )}
    </nav>
  );
}
